/**
 * Think page-agent drive state (M6).
 *
 * PageAgentBar / TargetHighlight / the copper "Think is driving" line all
 * subscribe here. Esc, a click on the main column, or Stop pauses Think.
 */

/** Tools that move the SPA. Observe/loop tools do not start the step bar. */
export const MISSION_UI_DRIVING_TOOLS = [
  "navigate",
  "click",
  "fill",
  "scroll",
  "open_file",
  "open_change",
  "open_issue",
  "set_filter",
  "draft_issue",
  "open_job",
  "request_upload",
] as const;

/** Once driving, every Mission client/loop tool increments the one counter. */
export const MISSION_DRIVE_COUNTED_TOOLS = [
  ...MISSION_UI_DRIVING_TOOLS,
  "get_page_context",
  "wait",
] as const;

export function isUiDrivingMissionTool(name: string): boolean {
  return (MISSION_UI_DRIVING_TOOLS as readonly string[]).includes(name);
}

export function isDriveCountedTool(name: string): boolean {
  return (MISSION_DRIVE_COUNTED_TOOLS as readonly string[]).includes(name);
}
export const MISSION_PAUSE_EVENT = "mission:pause";
export const MISSION_RESUME_EVENT = "mission:resume";
export const MISSION_END_EVENT = "mission:end";
/** Only STOP / Esc dispatch this. Idle chrome clear must never cancel the turn. */
export const MISSION_CANCEL_EVENT = "mission:cancel";
export const MISSION_DRIVE_EVENT = "mission:drive";
export const MISSION_HIGHLIGHT_EVENT = "mission:highlight";
export const MISSION_DRAFT_ISSUE_EVENT = "mission:draft-issue";
export const MISSION_SELECT_FILE_EVENT = "mission:select-file";
export const MISSION_SET_FILTER_EVENT = "mission:set-filter";

/** Resume with no new step ends the drive so chrome does not stick. */
export const MISSION_RESUME_IDLE_MS = 3000;

/** open_file / target highlight fades after this — drive chrome stays. */
export const MISSION_HIGHLIGHT_FADE_MS = 2500;

/** Plate copper text on void #050505 needs ≥4.5:1. `#C45C26` is 4.44:1. */
export const MISSION_COPPER_INK = "#D16E36";
export const MISSION_COPPER_LINE = "#C45C26";
/** Hint ink ≥4.5:1 on void (same as M5 #117). `#545457` is below. */
export const MISSION_HINT_INK = "#8B8B90";

export type MissionPauseReason = "esc" | "click" | "stop";
export type MissionCancelReason = "esc" | "stop";

export const CF_AGENT_CHAT_REQUEST_CANCEL = "cf_agent_chat_request_cancel";

/** Official useChat/useAgentChat: send only the new message. Think already persists history. */
export const CF_CHAT_FRAME_BUDGET_BYTES = 256 * 1024;

export type PrepareSendMessagesRequestInput = {
  id?: string;
  messages?: unknown[];
  trigger?: string;
  messageId?: string;
};

/**
 * AI SDK `prepareSendMessagesRequest` + Agents `useAgentChat` extraBody:
 * override `messages` to the last UI message so the WS frame stays under
 * Cloudflare's ~1 MiB limit. Think `_handleChatRequest` upserts the incoming
 * row onto SQLite history (`reconcileMessages` + `_persistIncomingMessage`).
 * https://ai-sdk.dev/docs/ai-sdk-ui/chatbot
 * https://ai-sdk.dev/cookbook/next/send-custom-body-from-use-chat
 */
export function lastMessageOnlyPrepareSend(opts: PrepareSendMessagesRequestInput): {
  body: { id?: string; trigger?: string; messageId?: string; messages: unknown[]; message?: unknown };
} {
  const messages = Array.isArray(opts.messages) ? opts.messages : [];
  const last = messages.length ? messages[messages.length - 1] : undefined;
  return {
    body: {
      id: opts.id,
      trigger: opts.trigger,
      messageId: opts.messageId,
      messages: last !== undefined ? [last] : [],
      message: last,
    },
  };
}

export function chatFrameByteLength(value: unknown): number {
  return new TextEncoder().encode(typeof value === "string" ? value : JSON.stringify(value)).length;
}

export type ServerMissionDrive = {
  active?: boolean;
  step?: number;
  action?: string | null;
  call?: string | null;
  requestId?: string | null;
  cancelled?: boolean;
};

export type UiToolPart = {
  type?: string;
  toolName?: string;
  toolCallId?: string;
  state?: string;
  input?: unknown;
  text?: string;
};

export type MissionHighlight = {
  label: string;
  missionId?: string;
  testid?: string;
  top: number;
  left: number;
  width: number;
  height: number;
};

export type MissionDriveSnapshot = {
  driving: boolean;
  paused: boolean;
  pauseReason: MissionPauseReason | null;
  cancelling: boolean;
  action: string | null;
  step: number;
  total: number;
  call: string | null;
  highlight: MissionHighlight | null;
  requestId: string | null;
};

const idle: MissionDriveSnapshot = {
  driving: false,
  paused: false,
  pauseReason: null,
  cancelling: false,
  action: null,
  step: 0,
  total: 0,
  call: null,
  highlight: null,
  requestId: null,
};

let state: MissionDriveSnapshot = { ...idle };
const listeners = new Set<(next: MissionDriveSnapshot) => void>();
let resumeIdleTimer: ReturnType<typeof setTimeout> | null = null;
let highlightFadeTimer: ReturnType<typeof setTimeout> | null = null;

type ThinkAgentSocket = {
  readyState?: number;
  send?: (data: string | Record<string, unknown>) => unknown;
  addEventListener?: (type: string, listener: () => void, opts?: { once?: boolean }) => void;
};

export type LiveThinkAgent = {
  send?: (data: string | Record<string, unknown>) => unknown;
  socket?: ThinkAgentSocket | null;
} | null | undefined;

let liveThinkAgent: LiveThinkAgent = null;
let queuedOfficialCancel: { type: string; id: string } | null = null;
let thinkStopGeneration = 0;
let officialCancelRetryTimers: ReturnType<typeof setTimeout>[] = [];
const OFFICIAL_CANCEL_RETRY_MS = [0, 16, 50, 100, 200] as const;

function clearResumeIdle(): void {
  if (resumeIdleTimer != null) {
    clearTimeout(resumeIdleTimer);
    resumeIdleTimer = null;
  }
}

function clearHighlightFade(): void {
  if (highlightFadeTimer != null) {
    clearTimeout(highlightFadeTimer);
    highlightFadeTimer = null;
  }
}

function scheduleHighlightFade(): void {
  clearHighlightFade();
  highlightFadeTimer = setTimeout(() => {
    highlightFadeTimer = null;
    if (!state.highlight) return;
    state = { ...state, highlight: null };
    emit();
  }, MISSION_HIGHLIGHT_FADE_MS);
}

function scheduleResumeIdle(): void {
  clearResumeIdle();
  resumeIdleTimer = setTimeout(() => {
    resumeIdleTimer = null;
    endMissionDriveIfIdle();
  }, MISSION_RESUME_IDLE_MS);
}

function emit() {
  const snap = getMissionDrive();
  for (const listener of listeners) listener(snap);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MISSION_DRIVE_EVENT, { detail: snap }));
  }
}

export function getMissionDrive(): MissionDriveSnapshot {
  return { ...state, highlight: state.highlight ? { ...state.highlight } : null };
}

export function subscribeMissionDrive(listener: (next: MissionDriveSnapshot) => void): () => void {
  listeners.add(listener);
  listener(getMissionDrive());
  return () => {
    listeners.delete(listener);
  };
}

export function resetMissionDrive(): void {
  clearResumeIdle();
  clearHighlightFade();
  clearThinkStop();
  clearOfficialCancelRetries();
  queuedOfficialCancel = null;
  liveThinkAgent = null;
  state = { ...idle };
  emit();
}

export function formatMissionCall(name: string, input: unknown): string {
  if (input === null || typeof input !== "object") return name;
  const rec = input as Record<string, unknown>;
  if (typeof rec.path === "string" && rec.path.trim()) return `${name} · ${rec.path.trim()}`;
  if (typeof rec.id === "string" && rec.id.trim()) return `${name} · ${rec.id.trim()}`;
  if (typeof rec.title === "string" && rec.title.trim()) return `${name} · ${rec.title.trim()}`;
  if (typeof rec.filter === "string" && rec.filter.trim()) {
    const page = typeof rec.page === "string" ? rec.page : "";
    return page ? `${name} · ${page}:${rec.filter}` : `${name} · ${rec.filter}`;
  }
  if (typeof rec.route === "string" && rec.route.trim()) return `${name} · ${rec.route.trim()}`;
  return name;
}

export function beginMissionDrive(action: string, call: string, step = 1): MissionDriveSnapshot {
  clearResumeIdle();
  clearThinkStop();
  state = {
    driving: true,
    paused: false,
    pauseReason: null,
    cancelling: false,
    action,
    step: Math.max(0, step),
    total: Math.max(0, step),
    call,
    highlight: state.highlight,
    requestId: state.requestId,
  };
  emit();
  return getMissionDrive();
}

export function advanceMissionDrive(action: string, call: string, step?: number): MissionDriveSnapshot {
  if (state.paused || isThinkTurnStopped()) return getMissionDrive();
  clearResumeIdle();
  const nextStep = typeof step === "number" ? Math.max(state.step, step) : state.step;
  state = {
    driving: true,
    paused: false,
    pauseReason: null,
    cancelling: state.cancelling,
    action,
    step: nextStep,
    total: Math.max(state.total, nextStep),
    call,
    highlight: state.highlight,
    requestId: state.requestId,
  };
  emit();
  return getMissionDrive();
}

export function setMissionHighlight(highlight: MissionHighlight | null): MissionDriveSnapshot {
  state = { ...state, highlight };
  if (highlight) scheduleHighlightFade();
  else clearHighlightFade();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MISSION_HIGHLIGHT_EVENT, { detail: highlight }));
  }
  emit();
  return getMissionDrive();
}

export function pauseMissionDrive(reason: MissionPauseReason): MissionDriveSnapshot {
  // Official cancel on the wire BEFORE local Paused chrome. Esc/click must
  // not return early just because isStreaming/isToolContinuation flipped.
  // agents ws-chat-transport `cancelActiveServerTurn` is a no-op when
  // `_activeServerTurnId` is null (first resume / client-tool gap).
  sendOfficialChatCancel(getLiveThinkAgent(), state.requestId);
  state = {
    ...state,
    driving: false,
    paused: true,
    cancelling: reason === "esc" || reason === "stop",
    pauseReason: reason,
    highlight: null,
  };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MISSION_PAUSE_EVENT, { detail: { reason } }));
  }
  emit();
  return getMissionDrive();
}

export function resumeMissionDrive(): MissionDriveSnapshot {
  state = {
    ...state,
    driving: true,
    paused: false,
    pauseReason: null,
    cancelling: false,
  };
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MISSION_RESUME_EVENT));
  }
  emit();
  scheduleResumeIdle();
  return getMissionDrive();
}

/** Clear spinner, step, copper line, highlight, and Stop. Idle — not paused. Never cancels Think. */
export function endMissionDrive(): MissionDriveSnapshot {
  clearResumeIdle();
  clearHighlightFade();
  const alreadyIdle = !state.driving && !state.paused && !state.highlight && !state.action && !state.call;
  state = { ...idle };
  if (!alreadyIdle && typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MISSION_END_EVENT));
  }
  emit();
  return getMissionDrive();
}

/** Ask the server to abort. Do not hide chrome until state.drive clears. */
export function requestCancelMissionDrive(reason: MissionCancelReason = "stop"): MissionDriveSnapshot {
  clearResumeIdle();
  // Queue the official frame on any live socket BEFORE local Stopped chrome.
  // Do not wait for busy / isStreaming / isToolContinuation / _activeServerTurnId.
  sendOfficialChatCancel(getLiveThinkAgent(), state.requestId);
  noteThinkStop();
  const already = state.cancelling;
  state = {
    ...state,
    cancelling: true,
    pauseReason: reason === "stop" ? "stop" : state.pauseReason,
  };
  if (!already && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(MISSION_CANCEL_EVENT, {
        detail: { reason, requestId: state.requestId },
      }),
    );
  }
  emit();
  return getMissionDrive();
}

/** Explicit STOP only after a server ack. Idle-clear must not call this. */
export function cancelMissionDrive(reason: MissionCancelReason = "stop"): MissionDriveSnapshot {
  return requestCancelMissionDrive(reason);
}

export function setMissionDriveRequestId(requestId: string | null): void {
  if (state.requestId === requestId) return;
  state = { ...state, requestId };
  emit();
}

/** Resume with no new begin/advance within `MISSION_RESUME_IDLE_MS`. */
export function endMissionDriveIfIdle(): MissionDriveSnapshot {
  if (state.driving && !state.paused) return endMissionDrive();
  return getMissionDrive();
}

/** Only real human pointer/keyboard input pauses Think — not Think-dispatched events. */
export function isHumanTakeoverEvent(event: { isTrusted?: boolean }): boolean {
  return event.isTrusted === true;
}

export function isMissionDriveActive(): boolean {
  return state.cancelling || (state.driving && !state.paused);
}

export function drivingBannerText(snap: MissionDriveSnapshot = getMissionDrive()): string {
  return "Think is driving";
}

/** Unique toolCallIds on the last assistant message (official UI parts). */
export function drivingStepLabel(snap: MissionDriveSnapshot = getMissionDrive()): string {
  return snap.step >= 1 ? `step ${snap.step}` : "";
}

export function thinkStatusIsBusy(status?: string | null): boolean {
  return status === "submitted" || status === "streaming";
}

/**
 * Official useAgentChat busy — this is the drive-bar predicate.
 * Drive-bar visibility is official `useAgentChat` (`@cloudflare/ai-chat/react`)
 * busy only — never last-assistant tool parts or a `done` tool.
 * Covers `status` (`submitted`/`streaming`), `isServerStreaming`, `isStreaming`,
 * `isToolContinuation`, and `isRecovering`. Official `isStreaming` is
 * `status === "streaming" || isServerStreaming` and misses `submitted`.
 * After `addToolOutput`, exported `isServerStreaming` can drop while
 * `isToolContinuation` is true (`agents/dist/chat/react.js` 421–427, 1098–1112;
 * `react.d.ts` 398–440). That official continuation flag holds *most* of the
 * client-tool gap. A remaining official hole: `resumeStream().finally` clears
 * `isToolContinuation` (`react.js` 410–415) before the next `onToolCall` effect
 * adds `pendingOnToolCallIds` (`react.js` 698–713), and `hasPendingClientToolCalls`
 * skips parts that are no longer `input-available` (`react.js` 1098–1110).
 * `isRecovering` is official busy (`isStreaming || isRecovering`).
 * Hide is decided only from the latest official `busy` passed to `onStreaming`.
 * After `addToolOutput` there is always a subsequent React render: the onToolCall
 * local `addToolOutput` (`react.js` 714–721) calls `sendToolOutputToServer`
 * (`react.js` 674–688) which `setClientToolResults` (686) and
 * `startToolContinuation` → `setIsToolContinuation(true)` (421–427, 687), then
 * `addToolResult` updates messages (716–720). The onToolCall promise `finally`
 * calls `finishOnToolCall` → `setPendingOnToolCallIds` (598–604, 736–738).
 * https://developers.cloudflare.com/agents/communication-channels/chat/autonomous-responses/
 */
export function officialThinkIsBusy(chat: {
  isStreaming?: boolean;
  isServerStreaming?: boolean;
  isToolContinuation?: boolean;
  isRecovering?: boolean;
  status?: string | null;
}): boolean {
  return (
    chat.isStreaming === true ||
    chat.isServerStreaming === true ||
    chat.isToolContinuation === true ||
    chat.isRecovering === true ||
    thinkStatusIsBusy(chat.status)
  );
}

/** Keep chrome while the stream is live or a client tool is still running. */
export function shouldHoldMissionDrive(status?: string | null, inFlightTools = 0): boolean {
  return thinkStatusIsBusy(status) || inFlightTools > 0;
}

/** End drive chrome when a busy Think turn settles to ready/error. */
export function shouldEndDriveOnThinkStatus(
  wasBusy: boolean,
  status?: string | null,
  inFlightTools = 0,
): boolean {
  if (shouldHoldMissionDrive(status, inFlightTools)) return false;
  return wasBusy && (status === "ready" || status === "error");
}

export type ThinkMissionDriveSession = {
  onStatus(status?: string | null): void;
  onStreaming(streaming: boolean): void;
  onUserMessage(): void;
  noteToolPart(name: string, input?: unknown, toolCallId?: string): void;
  noteUiParts(parts: readonly UiToolPart[]): void;
  onServerDrive(drive?: ServerMissionDrive | null): void;
  onTurnEnd(): void;
  beginTool(name: string, input?: unknown, toolCallId?: string): void;
  endTool(): void;
  inFlight(): number;
  dispose(): void;
};

export function driveFromAgentState(state: unknown): ServerMissionDrive | null {
  if (!state || typeof state !== "object") return null;
  const drive = (state as { drive?: unknown }).drive;
  if (!drive || typeof drive !== "object") return null;
  const rec = drive as ServerMissionDrive;
  return {
    active: rec.active === true,
    step: typeof rec.step === "number" ? rec.step : 0,
    action: typeof rec.action === "string" ? rec.action : null,
    call: typeof rec.call === "string" ? rec.call : null,
    requestId: typeof rec.requestId === "string" ? rec.requestId : null,
    cancelled: rec.cancelled === true,
  };
}

function sendAgentFrame(
  send: (data: string | Record<string, unknown>) => unknown,
  payload: Record<string, unknown>,
): boolean {
  try {
    send(JSON.stringify(payload));
    return true;
  } catch {
    try {
      send(payload);
      return true;
    } catch {
      return false;
    }
  }
}

/**
 * Official Agents/Think cancel frame. `useAgentChat.stop()` already sends
 * this when `_activeServerTurnId` is set. During the client-tool gap the
 * transport may have cleared that id (cloudflare/agents#1233), so STOP
 * also emits this frame so Think can `resetTurnState()`.
 */
/** Remember the shell-level useAgent so STOP can send without the dock. */
export function setLiveThinkAgent(agent: LiveThinkAgent): void {
  liveThinkAgent = agent ?? null;
  if (!queuedOfficialCancel) return;
  if (flushOfficialCancel(agent, queuedOfficialCancel)) {
    queuedOfficialCancel = null;
    clearOfficialCancelRetries();
  }
}

export function getLiveThinkAgent(): LiveThinkAgent {
  return liveThinkAgent;
}

export function noteThinkStop(): number {
  thinkStopGeneration += 1;
  return thinkStopGeneration;
}

export function clearThinkStop(): void {
  thinkStopGeneration = 0;
}

export function isThinkTurnStopped(): boolean {
  return thinkStopGeneration > 0;
}

export function sendOfficialChatCancel(
  agent: LiveThinkAgent,
  requestId?: string | null,
): boolean {
  const id =
    requestId && requestId.trim() && requestId.trim() !== "drive"
      ? requestId.trim()
      : typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `cancel-${Date.now()}`;
  const payload = { type: CF_AGENT_CHAT_REQUEST_CANCEL, id };
  if (flushOfficialCancel(agent, payload) || flushOfficialCancel(liveThinkAgent, payload)) {
    queuedOfficialCancel = null;
    clearOfficialCancelRetries();
    return true;
  }
  queuedOfficialCancel = payload;
  scheduleOfficialCancelRetry(payload);
  return false;
}

function clearOfficialCancelRetries(): void {
  for (const timer of officialCancelRetryTimers) clearTimeout(timer);
  officialCancelRetryTimers = [];
}

function scheduleOfficialCancelRetry(payload: { type: string; id: string }): void {
  clearOfficialCancelRetries();
  const schedule =
    typeof window !== "undefined" && typeof window.setTimeout === "function"
      ? window.setTimeout.bind(window)
      : typeof setTimeout === "function"
        ? setTimeout
        : null;
  if (!schedule) return;
  for (const ms of OFFICIAL_CANCEL_RETRY_MS) {
    officialCancelRetryTimers.push(
      schedule(() => {
        if (!queuedOfficialCancel) return;
        if (flushOfficialCancel(liveThinkAgent, queuedOfficialCancel)) {
          queuedOfficialCancel = null;
          clearOfficialCancelRetries();
        }
      }, ms),
    );
  }
  const socket = liveThinkAgent && typeof liveThinkAgent === "object" ? liveThinkAgent.socket : null;
  if (socket && socket.readyState === 0 && typeof socket.addEventListener === "function") {
    socket.addEventListener(
      "open",
      () => {
        if (!queuedOfficialCancel) return;
        if (flushOfficialCancel(liveThinkAgent, payload)) {
          queuedOfficialCancel = null;
          clearOfficialCancelRetries();
        }
      },
      { once: true },
    );
  }
}

function flushOfficialCancel(agent: LiveThinkAgent, payload: { type: string; id: string }): boolean {
  const trySend = (send: unknown): boolean => {
    if (typeof send !== "function") return false;
    return sendAgentFrame(send as (data: string | Record<string, unknown>) => unknown, payload);
  };
  const tryAgent = (next: LiveThinkAgent): boolean => {
    if (!next || typeof next !== "object") return false;
    // Must bind — PartySocket.send / useAgent.send use `this`. Unbound
    // `agent.send` throws in the first-resume window (production r16 miss).
    // Official transport does `this.agent.send(JSON.stringify({ type, id }))`
    // (ws-chat-transport sendCancelFrame).
    if (typeof next.send === "function" && trySend(next.send.bind(next))) return true;
    const socket = next.socket;
    if (socket && typeof socket.send === "function" && trySend(socket.send.bind(socket))) return true;
    return false;
  };
  // Production path only: bound agent.send / socket.send. Do not fall
  // through to a harness __qaSockets send (r15 false-pass).
  return tryAgent(agent) || tryAgent(liveThinkAgent);
}

let officialThinkStop: (() => void) | null = null;

export function registerOfficialThinkStop(fn: () => void): () => void {
  officialThinkStop = fn;
  return () => {
    if (officialThinkStop === fn) officialThinkStop = null;
  };
}

export function invokeOfficialThinkStop(): void {
  officialThinkStop?.();
}

export function toolNameFromUiPart(part: UiToolPart): string | null {
  if (part.type === "dynamic-tool") {
    return typeof part.toolName === "string" && part.toolName ? part.toolName : null;
  }
  if (typeof part.type === "string" && part.type.startsWith("tool-")) {
    return part.type.slice("tool-".length);
  }
  return typeof part.toolName === "string" && part.toolName ? part.toolName : null;
}

/** Unique counted toolCallIds in official assistant UI parts. Never a parallel increment. */
export function uniqueCountedToolCallIds(parts: readonly UiToolPart[]): string[] {
  const ids = new Set<string>();
  for (const part of parts) {
    const name = toolNameFromUiPart(part);
    if (!name || !isDriveCountedTool(name)) continue;
    const id =
      typeof part.toolCallId === "string" && part.toolCallId.trim()
        ? part.toolCallId.trim()
        : `${name}::${formatMissionCall(name, part.input)}`;
    ids.add(id);
  }
  return [...ids];
}

export function stepFromUiParts(parts: readonly UiToolPart[]): number {
  return uniqueCountedToolCallIds(parts).length;
}

/**
 * Bar lifetime follows official `useAgentChat` busy via `officialThinkIsBusy`.
 * Step = unique toolCallIds in last-assistant parts — never a separate increment.
 * Official idle ends the bar immediately. No `done` tool, no homemade
 * continuation hold, no settle timer, no hide microtask. `inFlight` is only
 * the live onToolCall body. Hide is decided only in `onStreaming(false)` when
 * `inFlight === 0` (dock `useLayoutEffect` passes this render's official busy).
 */
export function createThinkMissionDriveSession(): ThinkMissionDriveSession {
  let inFlight = 0;
  let streaming = false;
  let lastParts: UiToolPart[] = [];

  function applyParts(parts: readonly UiToolPart[]): void {
    lastParts = [...parts];
    const ids = uniqueCountedToolCallIds(parts);
    const live = streaming || inFlight > 0 || getMissionDrive().driving;
    // Hydrated history after reload is not a live turn.
    if (ids.length === 0) {
      return;
    }
    if (!live) return;
    let lastName = "think";
    let lastInput: unknown;
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const name = toolNameFromUiPart(parts[i]);
      if (!name || !isDriveCountedTool(name)) continue;
      lastName = name;
      lastInput = parts[i].input;
      break;
    }
    const call = formatMissionCall(lastName, lastInput);
    const snap = getMissionDrive();
    if (snap.driving) advanceMissionDrive(lastName, call, ids.length);
    else beginMissionDrive(lastName, call, ids.length);
  }

  return {
    onStatus(status) {
      this.onStreaming(shouldHoldMissionDrive(status, inFlight));
    },
    onStreaming(next) {
      streaming = next;
      if (next) {
        if (isThinkTurnStopped()) return;
        const snap = getMissionDrive();
        if (!snap.driving && !snap.paused) {
          beginMissionDrive(snap.action ?? "think", snap.call ?? "think", snap.step);
        }
        return;
      }
      // Official idle from this render. Hide only when no live onToolCall body.
      if (inFlight > 0) return;
      endMissionDrive();
    },
    onUserMessage() {
      streaming = true;
      lastParts = [];
      inFlight = 0;
      beginMissionDrive("think", "think", 0);
    },
    noteToolPart(name, input, toolCallId) {
      applyParts([
        ...lastParts,
        { type: `tool-${name}`, toolName: name, toolCallId, input, state: "input-available" },
      ]);
    },
    noteUiParts(parts) {
      applyParts(parts);
    },
    onServerDrive(drive) {
      if (drive?.requestId) setMissionDriveRequestId(drive.requestId);
      if (drive?.cancelled) {
        streaming = false;
        endMissionDrive();
      }
    },
    onTurnEnd() {
      streaming = false;
      endMissionDrive();
    },
    beginTool(name, input, toolCallId) {
      inFlight += 1;
      this.noteToolPart(name, input, toolCallId);
    },
    endTool() {
      inFlight = Math.max(0, inFlight - 1);
    },
    inFlight() {
      return inFlight;
    },
    dispose() {},
  };
}

export function highlightFromElement(
  el: Element | null,
  label: string,
  extra?: Pick<MissionHighlight, "missionId" | "testid">,
): MissionHighlight | null {
  if (!el || typeof el.getBoundingClientRect !== "function") return null;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) {
    return {
      label,
      missionId: extra?.missionId,
      testid: extra?.testid,
      top: rect.top,
      left: rect.left,
      width: 0,
      height: 0,
    };
  }
  return {
    label,
    missionId: extra?.missionId,
    testid: extra?.testid,
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  };
}

const PENDING_DRAFT_KEY = "mx.mission.draft-issue";

export function requestMissionDraftIssue(title: string, body: string): void {
  const detail = { title, body };
  try {
    sessionStorage.setItem(PENDING_DRAFT_KEY, JSON.stringify(detail));
  } catch {
    /* ignore quota / private mode */
  }
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MISSION_DRAFT_ISSUE_EVENT, { detail }));
}

export function consumePendingMissionDraft(): { title: string; body: string } | null {
  try {
    const raw = sessionStorage.getItem(PENDING_DRAFT_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(PENDING_DRAFT_KEY);
    const parsed = JSON.parse(raw) as { title?: unknown; body?: unknown };
    if (typeof parsed.title !== "string" || !parsed.title.trim()) return null;
    return { title: parsed.title, body: typeof parsed.body === "string" ? parsed.body : "" };
  } catch {
    return null;
  }
}

export function requestMissionSelectFile(path: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MISSION_SELECT_FILE_EVENT, { detail: { path } }));
}

export function requestMissionSetFilter(page: string, filter: string): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(MISSION_SET_FILTER_EVENT, { detail: { page, filter } }));
}
